import React, { useRef } from 'react';
import imageCompression from "browser-image-compression";
import { Send, Image as ImageIcon, X, Loader2 } from 'lucide-react'; // Renamed Image to ImageIcon
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

interface MessageInputProps {
  newMessage: string;
  setNewMessage: (message: string) => void;
  selectedImage: File | null;
  setSelectedImage: (file: File | null) => void;
  isUploading: boolean;
  sendMessageMutation: {
    isPending: boolean;
  };
  handleSendMessage: (e: React.FormEvent) => void;
  clearSelectedImage: () => void;
}

const MessageInput: React.FC<MessageInputProps> = ({
  newMessage,
  setNewMessage,
  selectedImage,
  setSelectedImage,
  isUploading,
  sendMessageMutation,
  handleSendMessage,
  clearSelectedImage,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleCompressedFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      };
      const compressedFile = await imageCompression(file, options);
      setSelectedImage(compressedFile);
    } catch (error) {
      // Fallback: use original file if compression fails
      setSelectedImage(file);
    }
  };

  // Removed outer padding div; parent in Messages.tsx now handles padding
  return (
    <form onSubmit={handleSendMessage} className="flex items-center space-x-2"> {/* Simplified form layout */}
      <input
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleCompressedFileChange}
        ref={fileInputRef}
      />

      <Button
        type="button"
        variant="ghost" // Changed to ghost for a softer look
        size="icon"
        className="flex-none p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700" // Adjusted padding & variant
        onClick={() => fileInputRef.current?.click()}
        aria-label="Bashkëngjit foto"
      >
        <ImageIcon className="h-5 w-5" /> {/* Ensured icon name is ImageIcon */}
      </Button>

      <div className="flex-1 flex items-center relative"> {/* Changed to relative for selected image overlay if needed later */}
        {selectedImage && (
          <div className="absolute left-0 top-1/2 -translate-y-1/2 -ml-1 bg-gray-100 dark:bg-gray-700 rounded-full p-1 flex items-center shadow text-xs z-10"> {/* More compact selected image indicator */}
            <ImageIcon className="h-3 w-3 text-brand-500" />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-4 w-4 ml-0.5 p-0 text-gray-500 hover:text-red-500"
              onClick={(e) => { e.stopPropagation(); clearSelectedImage(); }}
              aria-label="Hiq foton"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        )}

        <Input
          placeholder="Shkruaj një mesazh..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          className={`flex-1 ${selectedImage ? 'pl-8' : 'pl-3'} pr-3 py-2 md:py-2.5`} // Dynamic padding left
          autoComplete="off"
        />
      </div>

      <Button
        type="submit"
        size="icon" // Made send button icon-only for compactness
        className="bg-brand-500 hover:bg-brand-600 text-white p-2" // Adjusted padding
        disabled={
          isUploading ||
          sendMessageMutation.isPending ||
          (!newMessage.trim() && !selectedImage)
        }
        aria-label="Dërgo mesazhin"
      >
        {isUploading || sendMessageMutation.isPending ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <Send className="h-5 w-5" />
        )}
      </Button>
    </form>
  );
};

export default MessageInput;
